/**
 * TypeScript types for the Bank Transfer Receipt Queue feature (spec 033).
 * All types are derived from the deployed open-api.json (ReconciliationItemResponse,
 * DeclarationSummaryDto, ReceiptConfirmationResponse, ExtendDeadlineResponse).
 * Source of truth: open-api.json — not the contract YAML (bank-transfer-api.yaml).
 */

// ── Status & Stage unions ──────────────────────────────────────────────────────

export type BankTransferProcessStatus =
  | 'AWAITING_TRANSFER'
  | 'DECLARED_AWAITING_SETTLEMENT'
  | 'DEPOSIT_CONFIRMED'
  | 'AWAITING_BALANCE'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'LATE_FUNDS_REVIEW'
  | 'REFUND_REQUIRED'
  | 'CANCELLED';

export type TransferStage = 'DEPOSIT' | 'BALANCE' | 'FULL';

// ── Filter helpers ─────────────────────────────────────────────────────────────

export const ACTIVE_BANK_TRANSFER_STATUSES: readonly BankTransferProcessStatus[] = [
  'AWAITING_TRANSFER',
  'DECLARED_AWAITING_SETTLEMENT',
  'DEPOSIT_CONFIRMED',
  'AWAITING_BALANCE',
] as const;

export const TERMINAL_BANK_TRANSFER_STATUSES: readonly BankTransferProcessStatus[] = [
  'COMPLETED',
  'EXPIRED',
  'LATE_FUNDS_REVIEW',
  'REFUND_REQUIRED',
  'CANCELLED',
] as const;

// ── Declaration (maps to DeclarationSummaryDto) ────────────────────────────────

export interface DeclarationSummary {
  readonly declarationId: string;
  readonly stage: TransferStage;
  readonly declaredTransferDate: string;            // ISO 8601 date, e.g. '2026-06-28'
  readonly declaredAt: string;                      // ISO 8601 date-time
  readonly declaredAmount: number | null;           // may be null for older declarations
  readonly guestNote: string | null;
  readonly settlementConfirmationDueAt: string | null; // ISO 8601 date-time
}

// ── Queue item (maps to ReconciliationItemResponse) ────────────────────────────

export interface BankTransferQueueItem {
  readonly bookingId: string;
  readonly guestEmail: string;
  readonly guestName: string | null;
  readonly apartmentId: string;
  readonly apartmentName: string | null;
  readonly checkIn: string;                         // ISO 8601 date
  readonly checkOut: string;                        // ISO 8601 date
  readonly processStatus: BankTransferProcessStatus;
  readonly expectedStage: TransferStage;
  readonly expectedAmount: number;
  readonly currency: string;                        // ISO 4217, e.g. 'EUR'
  readonly paymentReference: string | null;         // null until instruction snapshot issued
  readonly declaration: DeclarationSummary | null;
  readonly transferDueAt: string | null;            // ISO 8601 date-time
  readonly updatedAt: string | null;
  readonly lateFundsStatus: string | null;
  readonly manualRefundObligationId: string | null;
  readonly processVersion: number;                  // resolved 2026-06-30
}

// ── Page (maps to ReconciliationSearchResponse) ────────────────────────────────

export interface BankTransferQueuePage {
  readonly items: readonly BankTransferQueueItem[];  // key is 'items', not 'content'
  readonly totalElements: number;
  readonly totalPages: number;
  readonly page: number;
  readonly size: number;
}

// ── Search params (local UI state → query params) ─────────────────────────────

export interface BankTransferSearchParams {
  readonly status?: BankTransferProcessStatus;
  readonly guestEmail?: string;
  readonly apartmentId?: string;
  readonly page?: number;
  readonly size?: number;
}

// ── Confirm Receipt ────────────────────────────────────────────────────────────

export interface ConfirmReceiptRequest {
  readonly stage: TransferStage;
  readonly expectedAmount: number;                  // from BankTransferQueueItem.expectedAmount
  readonly confirmedAmount: number;                 // editable; may differ from expectedAmount
  readonly currency: string;
  readonly paymentReference: string;                // from BankTransferQueueItem.paymentReference
  readonly note?: string;                           // max 1000 chars
  readonly expectedProcessVersion: number;          // from BankTransferQueueItem.processVersion
}

export interface ConfirmReceiptResponse {
  readonly confirmationId: string;
  readonly bookingId: string;
  readonly stage: TransferStage;
  readonly expectedAmount: number;
  readonly confirmedAmount: number;
  readonly currency: string;
  readonly paymentReference: string;
  readonly confirmedBy: string;
  readonly confirmedAt: string;                     // ISO 8601 date-time — set by server
  readonly note: string | null;
  readonly idempotencyKey: string;
  readonly resultingBookingStatus: string | null;
  readonly resultingProcessStatus: BankTransferProcessStatus | null;
}

// ── Extend Deadline ────────────────────────────────────────────────────────────

export interface ExtendDeadlineRequest {
  readonly stage: TransferStage;
  readonly newDeadline: string;                     // ISO 8601 date-time
  readonly reason: string;                          // 1–1000 chars
  readonly expectedProcessVersion: number;
}

export interface ExtendDeadlineResponse {
  readonly bookingId: string;
  readonly oldDeadline: string;                     // ISO 8601 date-time
  readonly newDeadline: string;                     // ISO 8601 date-time
  readonly processVersion: number;
}
